import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { notificationsApi } from '@/lib/api';
import { cn, formatDate } from '@/lib/utils';

const TYPE_COLORS = {
    info:    'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800',
    success: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800',
    warning: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800',
    error:   'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800',
} as const;

const TYPE_ICONS = {
    info: 'ℹ️',
    success: '✅',
    warning: '⚠️',
    error: '❌',
} as const;

export function NotificationBell() {
    const qc = useQueryClient();
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    const { data: count = 0 } = useQuery({
        queryKey: ['notifications', 'unread-count'],
        queryFn: () => notificationsApi.unreadCount(),
        refetchInterval: 30_000,
        refetchOnWindowFocus: true,
    });

    const { data: list = [], isLoading } = useQuery({
        queryKey: ['notifications', 'list'],
        queryFn: () => notificationsApi.list(),
        enabled: open,
    });

    const markRead = useMutation({
        mutationFn: (id: number) => notificationsApi.markRead(id),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['notifications'] });
        },
    });

    const markAllRead = useMutation({
        mutationFn: () => notificationsApi.markAllRead(),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['notifications'] });
        },
    });

    // Click-outside + ESC to close
    useEffect(() => {
        if (!open) return;
        function onDocClick(e: MouseEvent) {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        }
        function onKey(e: KeyboardEvent) {
            if (e.key === 'Escape') setOpen(false);
        }
        document.addEventListener('mousedown', onDocClick);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDocClick);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    return (
        <div ref={ref} className="relative">
            <button
                type="button"
                onClick={() => setOpen(o => !o)}
                aria-label={`Notificaciones${count > 0 ? ` (${count} no leídas)` : ''}`}
                aria-haspopup="true"
                aria-expanded={open}
                className="relative p-2 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                {count > 0 && (
                    <span
                        className="absolute -top-0.5 -right-0.5 inline-flex items-center justify-center min-w-[1.1rem] h-[1.1rem] px-1 rounded-full text-[10px] font-bold text-white bg-brand-red"
                        aria-hidden="true"
                    >
                        {count > 99 ? '99+' : count}
                    </span>
                )}
            </button>

            {open && (
                <div
                    role="dialog"
                    aria-label="Notificaciones"
                    className="absolute right-0 top-full mt-1 w-80 sm:w-96 max-h-[80vh] flex flex-col bg-white dark:bg-slate-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 z-30"
                >
                    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                        <h2 className="font-semibold text-gray-900 dark:text-white">Notificaciones</h2>
                        {count > 0 && (
                            <button
                                type="button"
                                onClick={() => markAllRead.mutate()}
                                disabled={markAllRead.isPending}
                                className="text-xs text-brand-blue hover:underline disabled:opacity-50"
                            >
                                Marcar todas como leídas
                            </button>
                        )}
                    </div>

                    <div className="flex-1 overflow-y-auto">
                        {isLoading ? (
                            <p className="p-6 text-sm text-center text-gray-500 dark:text-gray-400">
                                Cargando…
                            </p>
                        ) : list.length === 0 ? (
                            <p className="p-6 text-sm text-center text-gray-500 dark:text-gray-400">
                                No tienes notificaciones.
                            </p>
                        ) : (
                            <ul>
                                {list.slice(0, 20).map(n => {
                                    const unread = !n.read_at;
                                    const inner = (
                                        <div className={cn(
                                            'p-4 border-l-4 transition-colors',
                                            TYPE_COLORS[n.type] || TYPE_COLORS.info,
                                            unread ? 'opacity-100' : 'opacity-60'
                                        )}>
                                            <div className="flex items-start gap-2">
                                                <span aria-hidden="true">{TYPE_ICONS[n.type] || 'ℹ️'}</span>
                                                <div className="flex-1 min-w-0">
                                                    <p className="font-semibold text-sm text-gray-900 dark:text-white">
                                                        {n.title}
                                                    </p>
                                                    <p className="text-xs text-gray-700 dark:text-gray-300 mt-0.5 break-words">
                                                        {n.message}
                                                    </p>
                                                    <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-1">
                                                        {formatDate(n.created_at)}
                                                        {unread && <span className="ml-2 inline-block w-1.5 h-1.5 rounded-full bg-brand-red" aria-label="No leída" />}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                    const onClick = () => {
                                        if (unread) markRead.mutate(n.id);
                                    };
                                    return (
                                        <li key={n.id} className="border-b border-gray-100 dark:border-gray-700 last:border-0">
                                            {n.link ? (
                                                <Link to={n.link} onClick={() => { onClick(); setOpen(false); }} className="block hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                                    {inner}
                                                </Link>
                                            ) : (
                                                <button type="button" onClick={onClick} className="w-full text-left hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                                    {inner}
                                                </button>
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
